import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import { useState, useEffect } from "react";
import * as FaIcons from "react-icons/fa";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import Swal from "sweetalert2";

// Skeleton Card Component
const SkeletonCard = () => (
  <div className="card border mb-3">
    <div className="card-header">
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <h6 className="card-title mb-0 placeholder-glow">
            <span className="placeholder col-8"></span>
          </h6>
        </div>
      </div>
    </div>
    <div className="card-body">
      <div className="row row-cols-1 row-cols-md-3 row-cols-lg-5 g-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div className="col" key={i}>
            <div className="placeholder-glow">
              <span className="placeholder col-12"></span>
              <span className="placeholder col-8 mt-2"></span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default function WorkingHoursTab() {
  const [editingBranch, setEditingBranch] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [workSchedulePolicies, setWorkSchedulePolicies] = useState([]);
  const [permissions, setPermissions] = useState({});
  // Form state
  const [workSchedule, setWorkSchedule] = useState({
    id: "",
    branch_id: "",
    start_time: "09:00",
    end_time: "17:00",
    grace_period_end: "09:15",
    break_minutes: 60,
  });

  useEffect(() => {
    const perms = getPermissions();
    if (perms) {
      setPermissions(perms);
    }
  }, []);
  const [errors, setErrors] = useState({});
  const fetchBranches = async () => {
    try {
      const [branchesRes] = await Promise.all([apiUrl.get("/getBranches")]);

      if (branchesRes?.status) {
        setBranches(branchesRes.data.data || []);
      }
    } catch (error) {
      console.error("Failed to load data", error);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchWorkSchedule();
  }, []);

  const handleEdit = (schedule) => {
    setWorkSchedule({
      id: schedule.id,
      branch_id: schedule.branch_id,
      start_time: schedule.start_time?.slice(0, 5) || "09:00",
      end_time: schedule.end_time?.slice(0, 5) || "17:00",
      grace_period_end: schedule.grace_period_end?.slice(0, 5) || "09:15",
      break_minutes: schedule.break_minutes || 60,
    });
    setEditingBranch(schedule.branch_id);
    setOpenModal(true);
  };

  const handleAddNew = () => {
    setWorkSchedule({
      branch_id: "",
      start_time: "09:00",
      end_time: "17:00",
      grace_period_end: "09:15",
      break_minutes: 60,
    });
    setEditingBranch(null);
    setErrors({});
    setOpenModal(true);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!workSchedule.branch_id) newErrors.branch_id = "Branch is required";
    if (!workSchedule.start_time)
      newErrors.start_time = "Start time is required";
    if (!workSchedule.end_time) newErrors.end_time = "End time is required";
    if (!workSchedule.grace_period_end)
      newErrors.grace_period_end = "Grace period is required";
    if (!workSchedule.break_minutes && workSchedule.break_minutes !== 0)
      newErrors.break_minutes = "Break minutes is required";

    // Time validations
    if (
      workSchedule.grace_period_end <= workSchedule.start_time &&
      workSchedule.start_time &&
      workSchedule.grace_period_end
    ) {
      newErrors.grace_period_end = "Grace period must be after start time";
    }

    if (
      workSchedule.end_time <= workSchedule.grace_period_end &&
      workSchedule.grace_period_end &&
      workSchedule.end_time
    ) {
      newErrors.end_time = "End time must be after grace period";
    }

    if (workSchedule.break_minutes < 0)
      newErrors.break_minutes = "Break minutes cannot be negative";
    if (workSchedule.break_minutes > 60)
      newErrors.break_minutes = "Break cannot exceed 1 hours";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // fetch the work schedule policies regarding a branch
  const fetchWorkSchedule = async () => {
    try {
      setShowSkeleton(true);
      const response = await apiUrl.get("/hr/policies/fetch-work-schedules");
      if (response.status) {
        setWorkSchedulePolicies(response.data.data || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setShowSkeleton(false);
    }
  };
  // Save work schedule
  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        branch_id: parseInt(workSchedule.branch_id),
        start_time: workSchedule.start_time,
        end_time: workSchedule.end_time,
        grace_period_end: workSchedule.grace_period_end,
        break_minutes: parseInt(workSchedule.break_minutes),
      };
      let response;

      if (workSchedule.id && editingBranch) {
        response = await apiUrl.post(
          `/hr/policies/update-work-schedules/${workSchedule.id}`,
          payload
        );
      } else {
        response = await apiUrl.post(
          "/hr/policies/create-work-schedules",
          payload
        );
      }

      if (response.data.status) {
        Swal.fire({
          title: response.data.message,
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        setOpenModal(false);
        fetchWorkSchedule();
      } else {
        throw new Error(response.data.message || "Save failed");
      }
    } catch (error) {
      console.error("Save error:", error);
      if (error.response && error.response.data) {
        Swal.fire("Conflict", error.response.data.message, "warning");
      } else {
        Swal.fire(
          "Error",
          error.message || "Failed to save work schedule",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setWorkSchedule((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Format minutes to hours for display
  const formatMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
  };

  return (
    <>
      {permissions["manage_policies"] === 1 && (
        <div className="container-fluid px-2 py-3">
          {/* Header Card */}
          <div className="card border mb-1">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <h5 className="card-title d-flex align-items-center gap-2 mb-1">
                  <FaIcons.FaClock className="fs-5" />
                  Work Schedule Configuration
                </h5>
                <p className="card-text small text-muted mb-0">
                  Set required working hours, reporting time, and late
                  thresholds for each branch
                </p>
              </div>
              <button className="btn btn-primary" onClick={handleAddNew}>
                <FaIcons.FaPlus className="me-1" />
                Add Work Schedule
              </button>
            </div>
          </div>

          {/* Working Hours List */}
          <div className="card border">
            {showSkeleton ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : workSchedulePolicies?.length === 0 ? (
              <div className="text-center py-5">
                <FaIcons.FaClock className="fs-1 text-muted mb-3" />
                <p className="text-muted">No work schedules configured yet</p>
                <button className="btn btn-primary" onClick={handleAddNew}>
                  <FaIcons.FaPlus className="me-1" />
                  Add First Schedule
                </button>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {workSchedulePolicies.map((schedule, index) => (
                  <div key={index} className="card border mb-2">
                    <div className="card-header p-2">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h5 className="card-title mb-0 fw-bold text-green">
                            {schedule.branch_name}
                          </h5>
                        </div>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleEdit(schedule)}
                          >
                            <FaIcons.FaEdit className="me-1" /> Edit
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="card-body p-2">
                      <div className="row row-cols-1 row-cols-md-3 row-cols-lg-5 g-2">
                        <div className="col">
                          <label className="form-label">Required Hours</label>
                          <p className="h6 fw-semibold">
                            {formatMinutes(schedule?.required_work_minutes)}
                          </p>
                        </div>
                        <div className="col">
                          <label className="form-label">Reporting Time</label>
                          <p className="h6 fw-semibold">
                            {schedule?.start_time?.slice(0, 5)}
                          </p>
                        </div>
                        <div className="col">
                          <label className="form-label">Late After</label>
                          <p className="h6 fw-semibold text-danger">
                            {schedule?.grace_period_end?.slice(0, 5)}
                          </p>
                        </div>
                        <div className="col">
                          <label className="form-label">End Time</label>
                          <p className="h6 fw-semibold">
                            {schedule?.end_time?.slice(0, 5)}
                          </p>
                        </div>
                        <div className="col">
                          <label className="form-label">Break (min)</label>
                          <p className="h6 fw-semibold">
                            {schedule?.break_minutes}m
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Modal for Add/Edit Work Schedule */}
          {openModal && (
            <>
              {/* Backdrop */}
              <div
                className="modal-backdrop fade show"
                style={{
                  display: "block",
                  backgroundColor: "rgba(0,0,0,0.45)",
                  zIndex: 1040,
                }}
                onClick={() => setOpenModal(false)}
              />
              <div
                className="modal fade show d-block"
                tabIndex={-1}
                role="dialog"
                style={{ zIndex: 1050 }}
              >
                <div
                  className="modal-dialog modal-dialog-centered modal-lg"
                  style={{ maxWidth: 500 }}
                >
                  <div
                    className="modal-content border-0 rounded-4 shadow"
                    style={{ overflow: "hidden" }}
                  >
                    <div
                      className="modal-header"
                      style={{
                        background: "#f7f8fa",
                        borderBottom: "1px solid #eef2f6",
                      }}
                    >
                      <div>
                        <h5 className="modal-title" style={{ fontWeight: 700 }}>
                          {editingBranch
                            ? "Edit Work Schedule"
                            : "Add Work Schedule"}
                        </h5>
                        <div className="text-muted small">
                          Configure daily working hours for this branch
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setOpenModal(false)}
                      />
                    </div>

                    {/* Modal Body */}
                    <div className="modal-body">
                      <div className="d-flex flex-column gap-4 px-4 py-3">
                        {/* Branch Selection */}
                        <div className="row g-3">
                          <div className="col-12">
                            <label className="form-label">
                              Select Branch{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className={`form-select ${
                                errors.branch_id ? "is-invalid" : ""
                              }`}
                              value={workSchedule.branch_id}
                              onChange={(e) =>
                                handleInputChange("branch_id", e.target.value)
                              }
                            >
                              <option value="" disabled>
                                Select branch
                              </option>
                              {branches.map((branch) => (
                                <option key={branch.id} value={branch.id}>
                                  {branch.agencyname || branch.name}
                                </option>
                              ))}
                            </select>
                            {errors.branch_id && (
                              <div className="text-danger small mt-1">
                                {errors.branch_id}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Time Fields - Side by Side with Space */}
                        <div className="d-flex gap-4">
                          <div style={{ flex: 1 }}>
                            <label className="form-label">
                              Office Start Time{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="time"
                              className={`form-control ${
                                errors.start_time ? "is-invalid" : ""
                              }`}
                              value={workSchedule.start_time}
                              onChange={(e) =>
                                handleInputChange("start_time", e.target.value)
                              }
                            />
                            {errors.start_time && (
                              <div className="text-danger small mt-1">
                                {errors.start_time}
                              </div>
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <label className="form-label">
                              Late Grace Period Ends At{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="time"
                              className={`form-control ${
                                errors.grace_period_end ? "is-invalid" : ""
                              }`}
                              value={workSchedule.grace_period_end}
                              onChange={(e) =>
                                handleInputChange(
                                  "grace_period_end",
                                  e.target.value
                                )
                              }
                            />
                            {errors.grace_period_end && (
                              <div className="text-danger small mt-1">
                                {errors.grace_period_end}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* End Time + Break - 2 per row */}
                        <div className="d-flex gap-4">
                          <div style={{ flex: 1 }}>
                            <label className="form-label">
                              Office End Time{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="time"
                              className={`form-control ${
                                errors.end_time ? "is-invalid" : ""
                              }`}
                              value={workSchedule.end_time}
                              onChange={(e) =>
                                handleInputChange("end_time", e.target.value)
                              }
                            />
                            {errors.end_time && (
                              <div className="text-danger small mt-1">
                                {errors.end_time}
                              </div>
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <label className="form-label">
                              Break Duration (Minutes){" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className={`form-control ${
                                errors.break_minutes ? "is-invalid" : ""
                              }`}
                              value={workSchedule.break_minutes}
                              onChange={(e) =>
                                handleInputChange(
                                  "break_minutes",
                                  e.target.value
                                )
                              }
                              min="0"
                              max="240"
                            />
                            {errors.break_minutes && (
                              <div className="text-danger small mt-1">
                                {errors.break_minutes}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div
                      className="modal-footer"
                      style={{
                        background: "#f7f8fa",
                        borderTop: "1px solid #eef2f6",
                      }}
                    >
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setOpenModal(false)}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            {editingBranch
                              ? "Edit Work Schedule"
                              : "Save Work Schedule"}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
